/** Supported image file extensions (uppercase) */
const IMAGE_EXTENSIONS = ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP', 'SVG', 'BMP'];

/**
 * Extract file extension from filename.
 * @param fileName - The filename to extract extension from
 * @returns Uppercase extension (e.g., "PDF", "PNG") or "UNKNOWN" if no extension
 */
export function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  // No extension if: no dot, dot at start (dotfile), or dot at end
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return 'UNKNOWN';
  }
  return fileName.slice(lastDotIndex + 1).toUpperCase();
}

/**
 * Check if a file extension indicates an image.
 * @param extension - File extension (e.g., "PNG", "pdf")
 * @returns true if the extension is a recognized image format
 */
export function isImageExtension(extension: string): boolean {
  return IMAGE_EXTENSIONS.includes(extension.toUpperCase());
}

/**
 * Check if a file type string (extension) indicates an image.
 * Use this when you have a file type string like "PNG" or "JPEG".
 * @param type - File type/extension in uppercase (e.g., "PNG", "JPG")
 */
export function isImageType(type: string): boolean {
  return isImageExtension(type);
}

/**
 * Check if a file is an image based on its name or MIME type.
 * Prefers extension check, falls back to MIME type.
 * Works with File objects and objects with name/type properties.
 */
export function isImageFile(file: { type: string; name?: string }): boolean {
  // Primary: check extension from filename if available
  if (file.name) {
    const ext = getFileExtension(file.name);
    if (ext !== 'UNKNOWN' && isImageExtension(ext)) {
      return true;
    }
  }
  // Fallback: check MIME type
  return file.type.startsWith('image/');
}

/**
 * Generate a unique filename by appending _1, _2, etc. if the name already exists.
 * Uses underscores instead of parentheses to match backend filename sanitization.
 * @param fileName - The original filename (e.g., "document.pdf")
 * @param existingNames - Set or array of existing filenames to check against
 * @returns A unique filename (e.g., "document_1.pdf" if "document.pdf" exists)
 */
export function getUniqueFileName(
  fileName: string,
  existingNames: Set<string> | string[]
): string {
  const namesSet = existingNames instanceof Set ? existingNames : new Set(existingNames);

  if (!namesSet.has(fileName)) {
    return fileName;
  }

  // Split filename into base and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.slice(lastDotIndex) : '';

  // Find the next available number (using underscores for backend compatibility)
  let counter = 1;
  let newName = `${baseName}_${counter}${extension}`;

  while (namesSet.has(newName)) {
    counter++;
    newName = `${baseName}_${counter}${extension}`;
  }

  return newName;
}

/** The last path segment of a URL, when it carries an extension. */
function filenameFromUrl(url: string): string | null {
  try {
    const segment = new URL(url).pathname.split('/').pop();
    if (!segment) return null;
    const decoded = decodeURIComponent(segment);
    return decoded.includes('.') ? decoded : null;
  } catch {
    return null;
  }
}

/**
 * The download filename for a generated export: an explicit filename, else the
 * URL's last path segment, else the title, else `export`.
 *
 * IT LIVES HERE BECAUSE ITS TWO CONSUMERS ARE IN DIFFERENT ROOTS and one of them
 * must not pay for the other's graph. It used to sit in
 * `src/features/projects/timeline/evidence-tool-adapters/shared/exportDocumentPreview.ts`
 * beside preview wiring that reads the Work artefact renderer as a VALUE — and
 * that renderer draws a card published on the `@heidi/agents` `.` door, which is a
 * flat eager barrel. So `src/components/slide-builder/SlideToolbar.tsx`, which
 * wants a filename and nothing else, evaluated the whole Work UI surface and died
 * inside `dialog.styles.ts` under the partial typography mock its own suite
 * installs. This module imports nothing, which is what keeps that path cheap.
 */
export function resolveExportDocumentFilename(payload: {
  filename?: string;
  title?: string;
  url?: string;
}): string {
  const explicit = payload.filename?.trim();
  if (explicit) return explicit;

  if (payload.url) {
    const fromUrl = filenameFromUrl(payload.url);
    if (fromUrl) return fromUrl;
  }

  const title = payload.title?.trim();
  if (title) return title;

  return 'export';
}
